import { moroccoRegions, allPartiesIncludingNonParliamentary } from "@/data/parties";
import { getTranslatedProvince, getTranslatedRegion } from "@/utils/translations";

import { hasArabicCharacters, transliterateArToFr, transliterateFrToAr } from "./transliteration";

/**
 * Normalise une chaîne pour améliorer les correspondances (minuscules, sans accents)
 */
const normalizeString = (str: string) => {
  if (!str) return "";
  return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
};

/**
 * Tente d'extraire des candidats depuis un texte brut (Français ou Arabe).
 * Utilise des mots clés et la liste des régions/provinces pour déduire les attributs.
 */
export const extractCandidatesFromText = (rawText: string, defaultParty: string = "Indépendant") => {
  const extractedCandidates: any[] = [];
  
  // Diviser le texte en lignes ou blocs (séparés par des retours à la ligne, tirets ou virgules)
  const lines = rawText.split(/[\n\r;]+/).map(line => line.trim()).filter(line => line.length > 5);

  let currentRegion = "";
  let currentProvince = "";
  let currentListType = "locale";
  let currentParty = defaultParty;

  for (let line of lines) {
    const normLine = normalizeString(line);

    // Détection de Parti
    const foundParty = allPartiesIncludingNonParliamentary.find(p => 
      normLine.includes(normalizeString(p.acronym)) || 
      (p.name && normLine.includes(normalizeString(p.name))) ||
      (p.acronymAr && line.includes(p.acronymAr))
    );
    if (foundParty) currentParty = foundParty.acronym;

    // Détection de Région / Province
    let foundRegionObj = null;
    let foundProv = null;

    for (const reg of moroccoRegions) {
      if (normLine.includes(normalizeString(reg.name)) || (reg.nameAr && line.includes(reg.nameAr))) {
        foundRegionObj = reg;
      }
      for (const prov of reg.provinces) {
        // Obtenir le nom arabe via l'utilitaire si besoin, mais on va matcher le FR ou l'arabe traduit
        const provAr = getTranslatedProvince(prov, "ar");
        if (normLine.includes(normalizeString(prov)) || (provAr && line.includes(provAr))) {
          foundProv = prov;
          foundRegionObj = reg;
          break;
        }
      }
      if (foundProv) break;
    }

    if (foundRegionObj) currentRegion = foundRegionObj.name;
    if (foundProv) currentProvince = foundProv;

    // Détection du Type de Liste
    if (normLine.includes("regionale") || line.includes("جهوية")) {
      currentListType = "regionale";
    } else if (normLine.includes("locale") || line.includes("محلية")) {
      currentListType = "locale";
    }

    // Essayer d'extraire un Nom/Prénom (Heuristique basique : on prend ce qui reste)
    // Si la ligne contient ":" on suppose Format "Nom: XXX"
    let candidateName = "";
    if (line.includes(":")) {
      const parts = line.split(":");
      candidateName = parts[1].trim();
    } else {
      // Nettoyer la ligne des mots connus pour garder le nom
      let cleanLine = line;
      if (foundProv) cleanLine = cleanLine.replace(new RegExp(foundProv, 'ig'), "");
      const provAr = foundProv ? getTranslatedProvince(foundProv, "ar") : "";
      if (provAr) cleanLine = cleanLine.replace(new RegExp(provAr, 'g'), "");
      
      if (foundRegionObj) cleanLine = cleanLine.replace(new RegExp(foundRegionObj.name, 'ig'), "");
      if (foundRegionObj && foundRegionObj.nameAr) cleanLine = cleanLine.replace(new RegExp(foundRegionObj.nameAr, 'g'), "");
      
      if (foundParty) cleanLine = cleanLine.replace(new RegExp(foundParty.acronym, 'ig'), "");
      if (foundParty && foundParty.name) cleanLine = cleanLine.replace(new RegExp(foundParty.name, 'ig'), "");
      if (foundParty && foundParty.acronymAr) cleanLine = cleanLine.replace(new RegExp(foundParty.acronymAr, 'g'), "");

      cleanLine = cleanLine.replace(/candidat|candidate|liste|locale|regionale|مرشح|لائحة|محلية|جهوية/ig, "").trim();
      // Enlever les caractères spéciaux restants
      cleanLine = cleanLine.replace(/^[-\.,:;\|]+/, "").replace(/[-\.,:;\|]+$/, "").trim();
      
      if (cleanLine.length > 3 && cleanLine.split(" ").length <= 4) {
        candidateName = cleanLine;
      }
    }

    if (candidateName && candidateName.length > 2) {
      // Handle Arabic vs Latin detection and transliteration
      let fName = "";
      let lName = "";
      let fNameAr = "";
      let lNameAr = "";

      if (hasArabicCharacters(candidateName)) {
        const namePartsAr = candidateName.split(" ");
        lNameAr = namePartsAr[0] || "";
        fNameAr = namePartsAr.slice(1).join(" ") || "";
        
        // Transliterate to French
        const latinVersion = transliterateArToFr(candidateName);
        const namePartsFr = latinVersion.split(" ");
        lName = namePartsFr[0] || "";
        fName = namePartsFr.slice(1).join(" ") || "";
      } else {
        const namePartsFr = candidateName.split(" ");
        lName = namePartsFr[0] || "";
        fName = namePartsFr.slice(1).join(" ") || "";

        // Transliterate to Arabic
        const arabicVersion = transliterateFrToAr(candidateName);
        const namePartsAr = arabicVersion.split(" ");
        lNameAr = namePartsAr[0] || "";
        fNameAr = namePartsAr.slice(1).join(" ") || "";
      }

      extractedCandidates.push({
        id: "c_" + Date.now() + "_" + Math.floor(Math.random()*1000),
        firstName: fName || candidateName,
        lastName: fName ? lName : "",
        firstNameAr: fNameAr,
        lastNameAr: lNameAr,
        partyAcronym: currentParty,
        region: currentRegion || "Région Inconnue",
        province: currentProvince || "Province Inconnue",
        listType: currentListType,
        source: "admin",
        publishStatus: "published",
        bio: `Extrait automatiquement depuis le document/lien.`
      });
    }
  }

  // S'il n'y a pas de résultats précis, on peut retourner un candidat par ligne pour nettoyage manuel
  if (extractedCandidates.length === 0 && lines.length > 0) {
    return lines.slice(0, 50).map(l => ({
      id: "c_" + Date.now() + "_" + Math.floor(Math.random()*1000),
      firstName: l.substring(0, 30),
      lastName: "",
      firstNameAr: "",
      lastNameAr: "",
      partyAcronym: currentParty,
      region: "À préciser",
      province: "À préciser",
      listType: "locale",
      source: "admin",
      publishStatus: "published",
      bio: "Donnée brute : " + l
    }));
  }

  return extractedCandidates;
};
